import { PIF_CLASSIFICATION, PIF_FIELD_TYPE } from "../../../data/constants";
import { PostIfAttributeValueDto, PostIfPositionRulesDto } from "../../../types/pifDto";
import { pifEavFieldId, pifFieldId } from "./pifFocus";
import { parsePifDate } from "./pifDates";
import { getCurrentESTDate } from "../../../utils/dateHelper";
import {
  PIF_BLANK_FORM,
  PifEavValues,
  PifFlow,
  PifFormValues,
  PifGap,
  PifHasLicense,
  PIF_SSN_DIGIT_COUNT,
  isBridgedFieldKey,
  isPifPhoneFieldKey,
  isPifSsnEditable,
  toDigitsOnly,
} from "./types";

const LICENSE_NUMBER_PATTERN = /^([A-Za-z]\d{18}|\d{9})$/;

/**
 * A plain positive decimal - what the API can actually store.
 *
 * hours_per_week and requested_rate are free TextFields, so "" or "35 hrs" reaches
 * sProc_Portal_PostIFAttributeValues_IU, fails its TRY_CONVERT(DECIMAL(18,2)) check, and - because
 * that proc rejects the ENTIRE batch on one bad row - silently blocks every other answer on the
 * form from saving. Catch it here instead. Rejects symbols, letters, negatives and exponent
 * notation; allows "35", "35.5", "0.5" and ".5".
 */
export const isStorableDecimal = (value: string): boolean => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "" || trimmed === ".") return false;
  if (!/^[0-9.]+$/.test(trimmed)) return false;
  if (trimmed.split(".").length > 2) return false;
  return Number.isFinite(Number(trimmed));
};

export const isValidLicenseNumber = (licNum: string): boolean => LICENSE_NUMBER_PATTERN.test(licNum || "");

/**
 * Letters, plus the punctuation real names and cities use. Digits and other symbols are rejected.
 * Empty is valid — a required field's emptiness is a separate check.
 */
const PIF_NAME_PATTERN = /^[\p{L}\s.'’-]+$/u;
const PIF_NAME_HAS_LETTER = /\p{L}/u;

export const PIF_NAME_FORMAT_MESSAGE = "Use letters only.";
export const PIF_STATE_FORMAT_MESSAGE = "Use a two-letter state code.";
export const PIF_ZIP_FORMAT_MESSAGE = "Use a 5-digit ZIP, or ZIP+4.";
export const PIF_PHONE_FORMAT_MESSAGE = "Enter a 10-digit phone number.";
export const PIF_EMAIL_FORMAT_MESSAGE = "Enter an email address like name@example.com.";
export const PIF_LICENSE_FORMAT_MESSAGE = "Doesn't match the New York format yet.";
export const PIF_NUMBER_FORMAT_MESSAGE = "Enter a number.";
export const PIF_SSN_FORMAT_MESSAGE = "Enter all 9 digits before submitting.";

const filled = (value: string): string => String(value ?? "").trim();

/** First, middle, last, known as, and city. */
export const pifNameFormatMessage = (value: string): string | null => {
  const v = filled(value);
  if (!v) return null;
  if (!PIF_NAME_HAS_LETTER.test(v) || !PIF_NAME_PATTERN.test(v)) return PIF_NAME_FORMAT_MESSAGE;
  return null;
};

export const pifStateFormatMessage = (value: string): string | null => {
  const v = filled(value);
  if (!v) return null;
  if (!/^[A-Za-z]{2}$/.test(v)) return PIF_STATE_FORMAT_MESSAGE;
  return null;
};

export const pifZipFormatMessage = (value: string): string | null => {
  const v = filled(value);
  if (!v) return null;
  if (!/^\d{5}(-\d{4})?$/.test(v)) return PIF_ZIP_FORMAT_MESSAGE;
  return null;
};

export const pifPhoneFormatMessage = (value: string): string | null => {
  const v = filled(value);
  if (!v) return null;
  if (toDigitsOnly(v).length !== 10) return PIF_PHONE_FORMAT_MESSAGE;
  return null;
};

export const pifEmailFormatMessage = (value: string): string | null => {
  const v = filled(value);
  if (!v) return null;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return PIF_EMAIL_FORMAT_MESSAGE;
  return null;
};

/**
 * Bug 83251: a draft may be saved with a partial SSN (save-applicant sends whatever was typed),
 * but a hire may not be submitted with one. This is the inline prompt, and getPifGaps blocks the
 * final submit on it. Empty is valid - an SSN is not a required answer.
 */
export const pifSsnFormatMessage = (value: string): string | null => {
  const digits = toDigitsOnly(value);
  if (!digits || digits.length === PIF_SSN_DIGIT_COUNT) return null;
  return PIF_SSN_FORMAT_MESSAGE;
};

/** Empty is valid here; the required-license gap covers a missing number. */
export const pifLicenseFormatMessage = (licNum: string): string | null => {
  const v = filled(licNum);
  if (!v || isValidLicenseNumber(v)) return null;
  return PIF_LICENSE_FORMAT_MESSAGE;
};

/**
 * Format problems on an Additional-details field. Required-but-empty is not a format problem.
 * Locked, masked, and bridged fields are skipped — the user cannot correct them here.
 */
export const pifEavFormatMessage = (field: PostIfAttributeValueDto, value: string): string | null => {
  if (field.isLocked || field.isMasked || isBridgedFieldKey(field.fieldKey)) return null;
  const v = filled(value);
  if (!v) return null;

  if (isPifPhoneFieldKey(field.fieldKey)) return pifPhoneFormatMessage(v);

  if (field.dataType === PIF_FIELD_TYPE.DECIMAL) {
    if (!isStorableDecimal(v)) return PIF_NUMBER_FORMAT_MESSAGE;
    const n = Number(v);
    const under = isBound(field.minValue) && n < field.minValue;
    const over = isBound(field.maxValue) && n > field.maxValue;
    if (under || over) {
      if (isBound(field.minValue) && isBound(field.maxValue)) return `Between ${field.minValue} and ${field.maxValue}.`;
      if (under) return `At least ${field.minValue}.`;
      return `No more than ${field.maxValue}.`;
    }
  }

  return null;
};

/** Midnight of the current EST calendar day. Shared so "past" and "future" use the same today. */
const estToday = (): Date => {
  const estNow = getCurrentESTDate();
  return new Date(estNow.getFullYear(), estNow.getMonth(), estNow.getDate());
};

/**
 * True when the Intended start date is a past calendar day in EST — the app's one timezone
 * (CLAUDE.md) — not the supervisor's browser-local time, which the API and every other date on
 * the form don't share. Shared by PifSectionPosition (the "already passed" warning) and
 * getPifGaps below (the Notes-for-approvers requirement it triggers), so the two can never
 * disagree about what counts as "past".
 */
export const isPifStartDatePast = (startDate: string): boolean => {
  const value = parsePifDate(startDate);
  if (!value) return false;
  return value < estToday();
};

/**
 * True when Date of birth is after today in EST. Today is a valid birth date.
 * Bug 82243: the picker used to accept any later day, and a saved future date still submitted.
 */
export const isPifDobFuture = (dob: string): boolean => {
  const value = parsePifDate(dob);
  if (!value) return false;
  return value > estToday();
};

/** Bug 82808: how far back an Intended start date may go. */
export const PIF_START_DATE_MAX_YEARS_BACK = 30;

/**
 * The earliest Intended start date allowed — this EST calendar day, 30 years ago. On Feb 29 that
 * day doesn't exist and new Date() would roll it to Mar 1, so clamp to the month's last day.
 */
export const getPifStartDateMin = (): Date => {
  const estNow = getCurrentESTDate();
  const year = estNow.getFullYear() - PIF_START_DATE_MAX_YEARS_BACK;
  const month = estNow.getMonth();
  const min = new Date(year, month, estNow.getDate());
  return min.getMonth() === month ? min : new Date(year, month + 1, 0);
};

/**
 * True when the Intended start date is earlier than getPifStartDateMin. The picker's minDate stops
 * new picks; this catches a draft saved before the limit existed.
 */
export const isPifStartDateTooOld = (startDate: string): boolean => {
  const value = parsePifDate(startDate);
  return !!value && value < getPifStartDateMin();
};

/**
 * Bug 82665: a past Intended start date needs Notes for the approvers. Not once it is too old
 * (Bug 82808) — that date has to change, so asking to justify it would report one problem twice.
 * Shared by getPifGaps and PifSectionPay so the requirement and its prompt can't disagree.
 */
export const isPifStartDateNotesRequired = (startDate: string): boolean =>
  isPifStartDatePast(startDate) && !isPifStartDateTooOld(startDate);

/** A community-based DSP is always part-time and needs a service + client assignment. */
export const isCommunityDsp = (rules: PostIfPositionRulesDto | null): boolean =>
  rules?.classification === PIF_CLASSIFICATION.COMMUNITY_DSP;

/** Full-time is only offered for a schedule-based position that isn't community-based. */
export const isFullTimeAllowed = (rules: PostIfPositionRulesDto | null): boolean =>
  !!rules && rules.isScheduleBased && !isCommunityDsp(rules);

/**
 * The rate check. AC7: outside the *hiring* band is an inline error that blocks submit;
 * outside the wider *position* band is a warning only.
 *
 * A null bound means HR_PositionRates has no value for it, so there is nothing to check
 * against — we do not block on a band we don't know.
 */
export interface PifRateVerdict {
  hasRate: boolean;
  /** True when the rate is outside the hiring band and submit must be refused. */
  blocked: boolean;
  /** True when it clears the hiring band but sits outside the position band. */
  warn: boolean;
  message: string;
}

/** A band bound the API actually supplied. Null/undefined means "no value on file". */
export const isBound = (value: number | null | undefined): value is number => typeof value === "number";

/** The horizontal scale the pay-band bar is drawn against. */
export interface PifPayBandAxis {
  min: number;
  max: number;
  /** max - min, always > 0, so callers can divide by it without guarding. */
  span: number;
}

/**
 * The axis for the pay-band graphic: the union of every bound the API actually supplied.
 *
 * Bug 82694: this used to be the position band alone, so a position whose HR_PositionRates row
 * is missing a market rate - or missing entirely, which is most active positions - drew nothing
 * at all. No hiring fill, no rate marker, just an empty track and a "—/—" scale, which reads as
 * a broken graphic rather than as "no band on file". Taking the union means a position that has
 * only a hiring band still gets a usable bar to plot the rate against.
 *
 * Null when no bound is known, or when the known bounds collapse to a single point - there is
 * no span to place a marker along, and callers must draw no track at all rather than a
 * zero-width one.
 */
export const getPifPayBandAxis = (rules: PostIfPositionRulesDto | null): PifPayBandAxis | null => {
  if (!rules) return null;
  const lows = [rules.positionMin, rules.hiringMin].filter(isBound);
  const highs = [rules.positionMax, rules.hiringMax].filter(isBound);
  if (!lows.length || !highs.length) return null;
  const min = Math.min(...lows);
  const max = Math.max(...highs);
  return max > min ? { min, max, span: max - min } : null;
};

export const getPifRateVerdict = (rate: string, rules: PostIfPositionRulesDto | null): PifRateVerdict => {
  const value = parseFloat(rate);
  const hasRate = (rate || "").trim() !== "" && Number.isFinite(value);

  if (!rules) {
    return { hasRate, blocked: false, warn: false, message: "Pick a position to see the pay band." };
  }

  const { hiringMin, hiringMax, positionMin, positionMax } = rules;

  if (!hasRate) {
    const band =
      isBound(hiringMin) && isBound(hiringMax)
        ? `Hiring band: $${hiringMin}–$${hiringMax}.`
        : "No hiring band is set for this position.";
    return { hasRate, blocked: false, warn: false, message: band };
  }

  if (isBound(hiringMin) && value < hiringMin) {
    return {
      hasRate,
      blocked: true,
      warn: false,
      message: `Below the hiring minimum ($${hiringMin}). Change the rate or have HR raise the band this can't be submitted.`,
    };
  }
  if (isBound(hiringMax) && value > hiringMax) {
    return {
      hasRate,
      blocked: true,
      warn: false,
      message: `Above the hiring maximum ($${hiringMax}). Change the rate or have HR raise the band this can't be submitted.`,
    };
  }

  if (isBound(positionMin) && value < positionMin) {
    return { hasRate, blocked: false, warn: true, message: `Below the position minimum ($${positionMin}). HR and the Director will review it.` };
  }
  if (isBound(positionMax) && value > positionMax) {
    return { hasRate, blocked: false, warn: true, message: `Above the position maximum ($${positionMax}). HR and the Director will review it.` };
  }

  return { hasRate, blocked: false, warn: false, message: "Inside the hiring band no extra scrutiny." };
};

/** The dynamic fields that are required and still unanswered, plus a filled value in the wrong format. */
export const getPifEavGaps = (catalogue: PostIfAttributeValueDto[], eav: PifEavValues): PifGap[] => {
  const out: PifGap[] = [];
  for (const field of catalogue || []) {
    // A bridged key is validated by its own need(...) call below, so counting it here too would
    // report the same missing answer twice in the footer total.
    if (isBridgedFieldKey(field.fieldKey) || field.isLocked || field.isMasked) continue;
    const value = String(eav[field.fieldKey] ?? "").trim();
    const label = field.header || field.fieldKey;
    if (field.requiredForEntry && !value) {
      out.push({ label, sec: "details", field: pifEavFieldId(field.fieldKey) });
      continue;
    }
    const format = pifEavFormatMessage(field, value);
    if (format) out.push({ label: `${label}: ${format}`, sec: "details", field: pifEavFieldId(field.fieldKey) });
  }
  return out;
};

export const getPifGaps = (
  f: PifFormValues,
  flow: PifFlow,
  rules: PostIfPositionRulesDto | null,
  emailRequired: boolean,
  catalogue: PostIfAttributeValueDto[] = [],
  eav: PifEavValues = {},
): PifGap[] => {
  const out: PifGap[] = [];
  // `control` is the PifFormValues key of the input that answers the gap, which is not always the
  // key being checked: a reference dropdown is checked by its label (f.dept) but bound to its id
  // (deptId), and that is the control the error has to focus.
  const need = (v: string, label: string, sec: PifGap["sec"], control: keyof PifFormValues) => {
    if (!String(v || "").trim()) out.push({ label, sec, field: pifFieldId(control) });
  };

  if (flow !== "active") {
    need(f.firstName, "First name", "applicant", "firstName");
    need(f.lastName, "Last name", "applicant", "lastName");
    need(f.dob, "Date of birth", "applicant", "dob");
    // Only the new-applicant flow can edit this picker (it is disabled otherwise). Flagging a
    // future date on a locked record would block submit with no way to correct it.
    if (flow === "new" && isPifDobFuture(f.dob))
      out.push({ label: "Date of birth can't be in the future", sec: "applicant", field: pifFieldId("dob") });
    // Bug 83251: only where the SSN is an input - a masked, locked one cannot be corrected here.
    if (isPifSsnEditable(flow, f) && pifSsnFormatMessage(f.q))
      out.push({ label: "Social Security Number must be 9 digits", sec: "applicant", field: pifFieldId("q") });
    // Format checks run only where the field is editable. An active employee's record is locked,
    // and a bad value already on file must not become a gap they cannot fix.
    if (pifNameFormatMessage(f.firstName))
      out.push({ label: "First name must use letters", sec: "applicant", field: pifFieldId("firstName") });
    if (pifNameFormatMessage(f.middleName))
      out.push({ label: "Middle name must use letters", sec: "applicant", field: pifFieldId("middleName") });
    if (pifNameFormatMessage(f.lastName))
      out.push({ label: "Last name must use letters", sec: "applicant", field: pifFieldId("lastName") });
    need(f.addr1, "Street address", "contact", "addr1");
    need(f.city, "City", "contact", "city");
    if (pifNameFormatMessage(f.city))
      out.push({ label: "City must use letters", sec: "contact", field: pifFieldId("city") });
    need(f.state, "State", "contact", "state");
    if (pifStateFormatMessage(f.state))
      out.push({ label: "State must be a two-letter code", sec: "contact", field: pifFieldId("state") });
    need(f.zip, "ZIP", "contact", "zip");
    if (pifZipFormatMessage(f.zip))
      out.push({ label: "ZIP must be 5 digits or ZIP+4", sec: "contact", field: pifFieldId("zip") });
    if (!f.home && !f.cell)
      out.push({ label: "At least one phone number", sec: "contact", field: pifFieldId("home") });
    if (pifPhoneFormatMessage(f.home))
      out.push({ label: "Home phone must be 10 digits", sec: "contact", field: pifFieldId("home") });
    if (pifPhoneFormatMessage(f.cell))
      out.push({ label: "Cell phone must be 10 digits", sec: "contact", field: pifFieldId("cell") });
    if (emailRequired) need(f.email, "Personal email", "contact", "email");
    if (pifEmailFormatMessage(f.email))
      out.push({ label: "Personal email must be an email address", sec: "contact", field: pifFieldId("email") });
  }
  // Known as stays editable on every flow, including an active employee.
  if (pifNameFormatMessage(f.knownAs))
    out.push({ label: "Known as must use letters", sec: "applicant", field: pifFieldId("knownAs") });

  // The licence and vehicle questions only apply where the position allows driving. This gate MUST
  // stay in step with PifSectionContact's `mayOperateVehicle &&` render gate: requiring a field the
  // form does not show leaves the draft permanently unsubmittable with no way to answer it.
  if (rules?.mayOperateVehicle) {
    if (!f.hasLicense)
      out.push({ label: "Driver's license yes or no", sec: "contact", field: pifFieldId("hasLicense") });
    if (f.hasLicense === "yes") {
      need(f.licNum, "License number", "contact", "licNum");
      // The inline message used to be the only check, so a bad number still submitted.
      if (pifLicenseFormatMessage(f.licNum))
        out.push({ label: "License number doesn't match the New York format", sec: "contact", field: pifFieldId("licNum") });
    }
  }

  // No "Kind of hire" requirement (Bug 82085): the dropdown was removed from the Position section,
  // so requiring it would raise a missing-answer link focusing a control that no longer renders —
  // an unfillable gap, exactly like the licence gate above. `f.hireType` still exists and is seeded
  // from the flow, so Portal_PostIF.SwitchOrAddition is unaffected.
  need(f.dept, "Department", "position", "deptId");
  need(f.pos, "Position", "position", "posId");
  need(f.reportsTo, "Reports to", "position", "reportsToId");
  need(f.startDate, "Intended start date", "position", "startDate");
  if (isPifStartDateTooOld(f.startDate))
    out.push({
      label: `Intended start date can't be more than ${PIF_START_DATE_MAX_YEARS_BACK} years ago`,
      sec: "position",
      field: pifFieldId("startDate"),
    });
  // Bug 82798: the field renders on the Pay card (PifSectionPay), so the gap must
  // count against "pay". Tagging "position" would mark a step that no longer shows it.
  need(f.hours, "Hours per week", "pay", "hours");
  if (String(f.hours || "").trim() && !isStorableDecimal(f.hours))
    out.push({ label: "Hours per week must be a number", sec: "pay", field: pifFieldId("hours") });

  if (isCommunityDsp(rules)) {
    need(f.service, "Service", "position", "serviceId");
    need(f.client, "Individual / client", "position", "clientId");
  }

  need(f.rateType, "Rate type", "pay", "rateTypeId");
  need(f.rate, "Rate", "pay", "rate");
  if (String(f.rate || "").trim() && !isStorableDecimal(f.rate))
    out.push({ label: "Rate must be a number", sec: "pay", field: pifFieldId("rate") });
  if (getPifRateVerdict(f.rate, rules).blocked)
    out.push({ label: "Rate is outside the hiring band", sec: "pay", field: pifFieldId("rate") });

  // Bug 82665: the Position card warns "you've selected a past date... explain the reason",
  // but nothing enforced it — Notes for the approvers stayed optional, so the warning could be
  // ignored outright.
  if (isPifStartDateNotesRequired(f.startDate) && !String(f.notes || "").trim())
    out.push({ label: "Notes for the approvers", sec: "pay", field: pifFieldId("notes") });

  out.push(...getPifEavGaps(catalogue, eav));

  return out;
};

/**
 * How many answers this form requires in total — the denominator behind "7/15".
 *
 * Counted by running the gap check over a blank form rather than by hand, so the
 * numerator and denominator can never disagree about what "required" means. (The previous
 * hand-tallied constants were already one short of the fields the gap check asked for.)
 */
export const getPifRequiredTotal = (
  flow: PifFlow,
  rules: PostIfPositionRulesDto | null,
  emailRequired: boolean,
  catalogue: PostIfAttributeValueDto[] = [],
  hasLicense: PifHasLicense = "",
): number => {
  const blank: PifFormValues = { ...PIF_BLANK_FORM, hasLicense };
  return getPifGaps(blank, flow, rules, emailRequired, catalogue, {}).length;
};
